<template>
  <!-- Global opening feedback: a spinner from the click that opens a file or
       project until the editor lands (utils/openWithLoading.ts). Covers the
       page so the slow hop cannot be clicked again. -->
  <Teleport to="body">
    <div v-if="visible" class="opening-overlay" role="status" aria-live="polite" :aria-label="$t('Loading')">
      <div class="opening-card">
        <i class="fas fa-circle-notch fa-spin opening-spinner" aria-hidden="true"></i>
        <div v-if="name" class="opening-name" :title="name">{{ name }}</div>
        <div class="opening-sub">{{ $t('Loading') }}…</div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, watch } from 'vue';
import { useAssetsStore } from '@stores/assetsStore';

// Failsafe: never trap the user behind the overlay if the target page fails
// to clear it (e.g. it errors before its deep-link handling runs).
export const OPENING_FAILSAFE_MS = 30_000;

export default defineComponent({
  name: 'OpeningOverlay',
  setup() {
    const assetsStore = useAssetsStore();
    const visible = computed(() => assetsStore.openingFileName !== null);
    const name = computed(() => assetsStore.openingFileName || '');

    let failsafe: ReturnType<typeof setTimeout> | null = null;
    const stopFailsafe = () => {
      if (failsafe) clearTimeout(failsafe);
      failsafe = null;
    };
    watch(visible, (shown) => {
      stopFailsafe();
      if (shown) failsafe = setTimeout(() => assetsStore.clearOpening(), OPENING_FAILSAFE_MS);
    }, { immediate: true });
    onBeforeUnmount(stopFailsafe);

    return { visible, name };
  },
});
</script>

<style scoped>
.opening-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10, 15, 44, 0.55);
  backdrop-filter: blur(6px);
  display: grid;
  place-items: center;
  padding: 20px;
  z-index: 2000;
}
:global(html.dark-mode) .opening-overlay { background: rgba(0, 0, 0, 0.6); }
.opening-card {
  background: var(--adm-card, #fff);
  color: var(--adm-text-strong, #222);
  border: 1px solid var(--adm-border, #e4e8f5);
  border-radius: 14px;
  box-shadow: var(--adm-shadow, 0 16px 40px rgba(10, 15, 44, 0.14));
  padding: 28px 36px;
  max-width: min(22rem, 90vw);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
  font-family: 'IBM Plex Sans', sans-serif;
}
.opening-spinner { font-size: 2rem; color: var(--adm-accent, #003cff); }
.opening-name {
  font-weight: 700;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.opening-sub { color: var(--adm-muted, #8a92b8); font-size: 0.85rem; }
</style>
